'use strict';

class ServiceWorkerConnectListener {
  #isConnected = false;

  #connectCallbacks = [];

  constructor() {
    this.#isConnected = false;
    this.#connectCallbacks = [];
  }

  isConnected = () => this.#isConnected;

  registerConnectCallback = (callback) => {
    if (this.#isConnected) {
      callback();
      return;
    }

    this.#connectCallbacks.push(callback);
  };

  onConnect = () => {
    this.#isConnected = true;
    this.#connectCallbacks.forEach((callback) => callback());
  };

  onDisconnect = () => {
    this.#isConnected = false;
  };
}

angular.module('ClinicApp').factory('ServiceWorkerService', (
  $http,
  ClinicConfig,
  NotificationService,
  OCLogUtil,
  GrowlService,
) => {
  const NOTIFICATION_PERMISSION_GRANTED_TIMEOUT = 15000; // 15 seconds
  const ZENDESK_HELP_LINK = `
    <a
      href="https://opencarepractice.zendesk.com/hc/en-us/articles/37216837676948-How-to-Use-In-App-and-Desktop-Notifications"
      target="_blank"
    >
      How to Use In-App and Desktop Notifications
    </a>
  `;
  const SERVICE_WORKER_PATH = '/service-worker.js';
  const NOTIFICATION_STATUS = {
    NOT_ENABLED: 'not-enabled',
    DENIED: 'denied',
    NOT_GRANTED: 'not-granted',
    NOT_CONNECTED: 'not-connected',
    CONNECTED: 'connected',
  };

  const environment = ClinicConfig.ENV;
  const isEnabled = true;

  if (!isEnabled) {
    const promiseNoop = () => Promise.resolve(null);
    return {
      isEnabled: false,
      enableNotifications: promiseNoop,
      disableNotifications: promiseNoop,
      getStatus: () => 'not-enabled',
    };
  }

  const connectListener = new ServiceWorkerConnectListener();

  const sendTokenToApi = async (token) => {
    try {
      const userAgent = navigator?.userAgent || 'unknown';
      await $http.post(
        `${ClinicConfig.HOSTNAME}/notification/token`,
        { token, userAgent },
      );
    } catch (error) {
      OCLogUtil.rollbar.error('Error setting firebase token', error);
    }
  };

  const updateWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return;
      }

      await registration.update();
    } catch (error) {
      OCLogUtil.rollbar.error('Error updating service worker', error);
    }
  };

  const disableWorker = async (worker) => {
    if (!worker.isRegistered()) {
      return;
    }

    try {
      const success = await worker.unregister();
      if (!success) {
        OCLogUtil.rollbar.error('Error unregistering service worker');
        return;
      }

      connectListener.onDisconnect();
    } catch (error) {
      OCLogUtil.rollbar.error('Error unregistering service worker', error);
    }
  };

  /**
   * Connects the service worker instance to the real service worker
   * This can be called either after the service worker is registered
   * or any page load after the service worker is already registered
   */
  const checkServiceWorker = async (worker) => {
    try {
      const isRegistered = await worker.isRegistered();
      if (!isRegistered) {
        return;
      }

      if (!isNotificationPermissionGranted()) {
        await disableWorker(worker);
        return;
      }

      const token = await worker.connect();
      NotificationService.setToken(token);
      await updateWorker();
      connectListener.onConnect();

      // We can fire and forget here because the token is not needed
      // for anything else on the client side.
      // If it fails, it will be retried on the next page load.
      sendTokenToApi(token);
    } catch (error) {
      OCLogUtil.rollbar.error('Error checking service worker', error);
    }
  };

  /**
   * Creates an instance of the service worker API
   * This does not register the service worker and is
   * safe to call multiple times
   */
  const createServiceWorker = () => {
    const worker = ocServiceWorker.createServiceWorker({
      environment,
      serviceWorkerPath: SERVICE_WORKER_PATH,
      enableFirebase: true,
      onNotification: (notification) => NotificationService.addNotification(notification),
    });

    checkServiceWorker(worker);

    return worker;
  };

  /**
   * Checks if the browser notification permission is granted
   * @type {() => boolean}
   */
  const isNotificationPermissionGranted = ocServiceWorker.isNotificationPermissionGranted;

  /**
   * Checks if the browser notification permission is denied
   * @type {() => boolean}
   */
  const isNotificationPermissionDenied = ocServiceWorker.isNotificationPermissionDenied;

  /**
   * Registers a service worker with the browser
   * Only needed if the service worker is not already registered EVER
   * This does not need to be called every time the page is loaded
   */
  const registerServiceWorker = async (worker) => {
    if (!isNotificationPermissionGranted()) {
      throw new Error('Notification permission not granted');
    }

    try {
      await worker.register();
      await checkServiceWorker(worker);
    } catch (error) {
      OCLogUtil.rollbar.error('Error registering service worker', error);
    }
  };

  /**
   * Requests the browser notification permission
   * If the user has already granted or denied permission,
   * it will return immediately with their decision.
   *
   * This should only be called by a user action indicating
   * that they want to enable notifications.
   * Otherwise we risk them denying it, which would mean
   * we can't ask again unless they reset their permissions
   * @returns {Promise<boolean>}
   */
  const requestNotificationPermission = async () => {
    const isAlreadyGranted = isNotificationPermissionGranted();
    if (isAlreadyGranted) {
      return true;
    }

    const isAlreadyDenied = isNotificationPermissionDenied();
    if (isAlreadyDenied) {
      OCLogUtil.rollbar.error('Notification permission was previously denied');
      return false;
    }

    const timer = setTimeout(() => {
      OCLogUtil.rollbar.error(`Notification permission not granted after ${NOTIFICATION_PERMISSION_GRANTED_TIMEOUT}ms`);
      GrowlService.error(
        [
          "It's taking a while to enable notifications.",
          'Please see our help article to find out how to enable notifications for your browser.',
          ZENDESK_HELP_LINK,
        ].join('<br/>'),
        {
          title: 'Error enabling notifications',
          ttl: -1,
        },
      );
    }, NOTIFICATION_PERMISSION_GRANTED_TIMEOUT);

    const isGranted = await ocServiceWorker.requestNotificationPermission();
    clearTimeout(timer);
    if (!isGranted) {
      OCLogUtil.rollbar.error('Notification permission was not granted after request');
    }

    return isGranted;
  };

  // Create an instance of the service worker API
  const serviceWorker = createServiceWorker();

  const enableNotifications = async () => {
    if (isNotificationPermissionDenied()) {
      throw new Error('Notification permission denied');
    }

    if (!isNotificationPermissionGranted()) {
      await requestNotificationPermission();
    }

    await registerServiceWorker(serviceWorker);
  };

  const getStatus = () => {
    if (!isEnabled) {
      return NOTIFICATION_STATUS.NOT_ENABLED;
    }

    if (isNotificationPermissionDenied()) {
      return NOTIFICATION_STATUS.DENIED;
    }

    if (!isNotificationPermissionGranted()) {
      return NOTIFICATION_STATUS.NOT_GRANTED;
    }

    if (!connectListener.isConnected()) {
      return NOTIFICATION_STATUS.NOT_CONNECTED;
    }

    return NOTIFICATION_STATUS.CONNECTED;
  };

  return {
    isEnabled,
    enableNotifications,
    disableNotifications: () => disableWorker(serviceWorker),
    getStatus,
    getZendeskHelpLink: () => ZENDESK_HELP_LINK,
    NOTIFICATION_STATUS,
  };
});
